/**
 * The photo sections a receiving log's photos are organised into, and the file
 * naming that carries a section through an upload.
 *
 * The sections are not a server-side concept: the mobile flow names each captured
 * file `<fileLabel>-<n>` and recovers the grouping by matching that prefix back
 * (see ShipmentPhotosComponent's `_rehydrateFromDraft`), which is also what lets
 * the backend label what it stores. These rules are that convention, extracted so
 * the log-table's "update photos" editor sections and names photos identically to
 * the mobile flow instead of keeping a second, drifting copy.
 */

/** Stable identifier for a section (not shown to the user). */
export type LogPhotoSectionKey =
  | 'packing-slip'
  | 'shipping-label'
  | 'parts-materials'
  | 'full-shipment'
  | 'extra-photo';

export interface LogPhotoSection {
  key: LogPhotoSectionKey;
  /** Heading shown on the section's card. */
  title: string;
  /** One-line prompt shown while the section is empty. */
  hint: string;
  /**
   * Filename prefix for photos added to this section — the whole point of the
   * section. Equal to the key by construction, kept separate so a title can be
   * reworded without renaming files.
   */
  fileLabel: string;
  /**
   * Takes the full width of the editor rather than sharing a row. True for the
   * slip (many pages) and extras (open-ended); the three shipment categories sit
   * side by side, as they do on desktop in the mobile flow.
   */
  wide: boolean;
}

/**
 * Every section, in the order the mobile flow presents them: packing slip first,
 * then the three shipment categories, then extras.
 */
export const LOG_PHOTO_SECTIONS: readonly LogPhotoSection[] = [
  {
    key: 'packing-slip',
    title: 'Packing slip',
    hint: 'Every page of the slip for this delivery',
    fileLabel: 'packing-slip',
    wide: true,
  },
  {
    key: 'shipping-label',
    title: 'Shipping label',
    hint: 'Include the tracking barcode',
    fileLabel: 'shipping-label',
    wide: false,
  },
  {
    key: 'parts-materials',
    title: 'Parts / material',
    hint: 'Close-up of contents',
    fileLabel: 'parts-materials',
    wide: false,
  },
  {
    key: 'full-shipment',
    title: 'Full shipment',
    hint: 'Stand back — show the whole delivery',
    fileLabel: 'full-shipment',
    wide: false,
  },
  {
    key: 'extra-photo',
    title: 'Extra photos',
    hint: 'Anything else worth recording',
    fileLabel: 'extra-photo',
    wide: true,
  },
];

/**
 * The bare filename of a photo, without the blob path or the extension —
 * `logs/1234/packing-slip-2.jpg` becomes `packing-slip-2`. Both a fetched
 * photo's `blobName` (a full blob path) and a pending photo's `name` (already
 * bare) go through this.
 */
function baseName(name: string): string {
  const fileName = name.split(/[\\/]/).pop() ?? '';
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

/**
 * Which section a photo belongs to, read off its file name.
 *
 * @param name A photo's `blobName` or `name`; a blob path is fine.
 * @returns The matching section key, or null for a name that predates the
 *          convention (e.g. `IMG_1234.jpg`) — those stay unsectioned rather than
 *          being guessed into a bucket.
 */
export function sectionKeyForPhotoName(name: string | undefined | null): LogPhotoSectionKey | null {
  if (!name) { return null; }
  const base = baseName(name).toLowerCase();
  for (const section of LOG_PHOTO_SECTIONS) {
    // `-n` suffix expected, but a suffix-less `packing-slip.jpg` from an older
    // upload still reads as that section.
    if (base === section.fileLabel || new RegExp(`^${section.fileLabel}-\\d+$`).test(base)) {
      return section.key;
    }
  }
  return null;
}

/**
 * The number a newly added photo should carry in a section, so its name cannot
 * collide with a photo already stored on the log.
 *
 * Continues from the highest `-n` already in use rather than counting photos: a
 * log whose `packing-slip-1` was deleted must not hand `-1` out a second time,
 * since names are what tie a stored photo to its section.
 *
 * @param existingNames Names (or blob paths) of every photo already on the log,
 *                      plus any pending ones — sections are filtered here, so
 *                      callers can pass the lot.
 * @param fileLabel The section's filename prefix.
 * @returns The next index, starting at 1 for an unused section.
 */
export function nextPhotoIndex(existingNames: (string | undefined | null)[], fileLabel: string): number {
  const pattern = new RegExp(`^${fileLabel}-(\\d+)$`);
  let highest = 0;
  for (const name of existingNames) {
    if (!name) { continue; }
    const match = pattern.exec(baseName(name).toLowerCase());
    if (match) {
      highest = Math.max(highest, Number(match[1]));
    }
  }
  return highest + 1;
}

/**
 * The upload name for a photo in a section — `<fileLabel>-<n>.<ext>`, the form
 * both the mobile flow and the backend's grouping expect.
 */
export function photoFileName(fileLabel: string, index: number, extension: string): string {
  return `${fileLabel}-${index}.${extension}`;
}

/**
 * The `kind` an update-upload request must carry for a section, or undefined for
 * the sections the endpoint files by default.
 *
 * Only the packing slip is labelled: the three shipment categories and extras are
 * all ordinary proof photos, distinguished from each other by name alone. Damage
 * photos are not a section here — they hang off a receipt row, not off the log
 * (see `damagePhotoFileName`).
 */
export function uploadKindForSection(key: LogPhotoSectionKey): 'Slip' | undefined {
  return key === 'packing-slip' ? 'Slip' : undefined;
}

/**
 * The upload name for a damage photo: `damage-{position}-{n}.{ext}`.
 *
 * `position` is a 0-based index into the `receiptIds` array sent with the same
 * request — NOT a receipt id. The array and these names must be built from one
 * source in one pass, or each photo attaches to the wrong PO line. Same
 * convention as the submit path's `DamagePhotos` field.
 *
 * @param position Index of the photo's receipt within the request's `receiptIds`
 * @param index 1-based photo number within that receipt
 * @param extension File extension, without the dot
 */
export function damagePhotoFileName(position: number, index: number, extension: string): string {
  return `damage-${position}-${index}.${extension}`;
}
